// Selection Controller - centralizes project/page selection, persistence, and events

import type { AppContext, UIStateAPI } from "../types/index.js";
import { getAllExampleProjects } from "./example-projects.js";
import { switchCurrentProject } from "../api";
import type { Session } from "../types/session";
import type {
  AppLanguage,
  Page,
  PageSummary,
  ProjectBootstrapPayload,
  ProjectSummary,
  ProjectType,
  Sketch,
  SketchSummary,
} from "../types/project-types";
import { getSubtoolsForMode } from "./tool-mode-map";

interface ProjectChangedEventData {
  readonly projectId: string;
  readonly projectName: string;
  readonly projectType: ProjectType;
  readonly bootstrap: ProjectBootstrapPayload;
  readonly pages: readonly PageSummary[];
  readonly selectedPageId: string | null;
  readonly selectedPage?: Page;
  readonly sketches: readonly SketchSummary[];
  readonly selectedSketchId: string | null;
  readonly selectedSketch?: Sketch;
  readonly selectedMode: "edit" | "sketch" | "ds" | "cms" | null;
  readonly availableLanguages: readonly AppLanguage[];
  readonly preferredTool?: string | null;
}

interface PageChangedEventData {
  readonly pageId: string;
  readonly pageName: string;
  readonly pageType?: PageSummary["type"];
  readonly pageSummary?: PageSummary;
  readonly page?: Page;
}

interface SketchChangedEventData {
  readonly projectId: string;
  readonly sketchId: string;
  readonly sketchName: string;
  readonly sketchSummary?: SketchSummary;
  readonly sketch?: Sketch;
}

interface ProjectsAvailableEventData {
  readonly projects: readonly ProjectSummary[];
  readonly selectedProjectId: string | null;
}

export interface SelectionControllerAPI {
  readonly destroy: () => void;
}

const SELECTED_PROJECT_INDEX_KEY = "ui:selectedProjectExampleIndex";
const pageIndexKeyForExampleIndex = (index: number) =>
  `ui:selectedPageIndex:exampleIndex:${index}`;

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "page";

const ensureDate = (value: unknown): Date =>
  value instanceof Date
    ? value
    : new Date((value as string | number | undefined) ?? Date.now());

const ensureLanguages = (value: unknown): AppLanguage[] => {
  if (Array.isArray(value) && value.every((lang) => typeof lang === "string")) {
    return value as AppLanguage[];
  }
  return ["en"];
};

const toProjectSummary = (
  bootstrap: ProjectBootstrapPayload
): ProjectSummary => ({
  id: bootstrap.id,
  name: bootstrap.name,
  description: bootstrap.description,
  projectType: bootstrap.projectType,
  createdAt: ensureDate(bootstrap.createdAt),
  updatedAt: ensureDate(bootstrap.updatedAt),
});

const coerceProjectSummary = (summary: any): ProjectSummary => ({
  id: String(summary.id),
  name: String(summary.name ?? "Untitled Project"),
  description:
    typeof summary.description === "string" ? summary.description : undefined,
  projectType: (summary.projectType ?? "website") as ProjectType,
  createdAt: ensureDate(summary.createdAt),
  updatedAt: ensureDate(summary.updatedAt),
});

const createPageSummary = (
  legacyPage: any,
  projectId: string,
  order: number
): PageSummary => ({
  id: String(legacyPage.id),
  projectId,
  name: String(legacyPage.name ?? "Untitled Page"),
  slug:
    typeof legacyPage.slug === "string"
      ? legacyPage.slug
      : slugify(String(legacyPage.name ?? "")),
  order:
    typeof legacyPage.order === "number" && Number.isFinite(legacyPage.order)
      ? legacyPage.order
      : order,
  type: (legacyPage.type ?? "component") as PageSummary["type"],
  version: String(legacyPage.version ?? "v1"),
  draft: Boolean(legacyPage.draft),
  updatedAt: ensureDate(legacyPage.modified ?? legacyPage.created),
  previews: Array.isArray(legacyPage.previews)
    ? legacyPage.previews
        .filter(
          (item: any) =>
            item &&
            typeof item === "object" &&
            typeof item.kind === "string" &&
            typeof item.url === "string"
        )
        .map((item: any) => ({
          kind: item.kind as PageSummary["previews"][number]["kind"],
          url: String(item.url),
          w: Number(item.w ?? 0) || 0,
          h: Number(item.h ?? 0) || 0,
          createdAt: ensureDate(item.createdAt),
        }))
    : undefined,
});

const createPageDetail = (legacyPage: any, summary: PageSummary): Page => ({
  id: summary.id,
  projectId: summary.projectId,
  name: summary.name,
  slug: summary.slug,
  order: summary.order,
  type: summary.type,
  description:
    typeof legacyPage.description === "string"
      ? legacyPage.description
      : undefined,
  version: summary.version,
  draft: summary.draft,
  data:
    legacyPage.data && typeof legacyPage.data === "object"
      ? { ...legacyPage.data }
      : {},
  createdAt: ensureDate(legacyPage.created),
  updatedAt: summary.updatedAt,
});

export const createSelectionController = (
  appContext: AppContext,
  uiState: UIStateAPI
): SelectionControllerAPI => {
  let selectedProjectIndex = 0;
  let isDestroyed = false;
  let sessionCache: Session | null = null;

  const examples = getAllExampleProjects();

  // Seed from session manager if already available
  try {
    const sessMgr = (appContext as any)?.session;
    if (sessMgr && typeof sessMgr.getSession === "function") {
      sessionCache = (sessMgr.getSession() as Session) ?? null;
    }
  } catch {
    sessionCache = null;
  }

  const readStoredProjectIndex = (): void => {
    if (sessionCache) return;
    try {
      const raw = localStorage.getItem(SELECTED_PROJECT_INDEX_KEY);
      if (!raw) return;
      const idx = Number(raw);
      if (Number.isInteger(idx) && idx >= 0 && idx < examples.length) {
        selectedProjectIndex = idx;
      }
    } catch {
      // ignore persistence failures
    }
  };

  const readStoredPageIndex = (index: number): number | null => {
    try {
      const raw = localStorage.getItem(pageIndexKeyForExampleIndex(index));
      if (raw == null) return null;
      const idx = Number(raw);
      if (Number.isInteger(idx) && idx >= 0) return idx;
      return null;
    } catch {
      return null;
    }
  };

  const writeStoredPageIndex = (index: number, pageIndex: number): void => {
    try {
      localStorage.setItem(
        pageIndexKeyForExampleIndex(index),
        String(pageIndex)
      );
    } catch {
      // ignore persistence failures
    }
  };

  const buildExampleBootstrap = (
    exampleIndex: number
  ): ProjectBootstrapPayload => {
    const example = examples[exampleIndex] ?? examples[0] ?? null;
    const legacyProject = example?.project.getProject();
    if (!legacyProject) {
      const now = new Date();
      return {
        id: `example-${exampleIndex}`,
        name: "Example Project",
        projectType: "website",
        createdAt: now,
        updatedAt: now,
        pages: [],
        sketches: [],
        selectedSketch: undefined,
      };
    }

    const projectId = String(legacyProject.metadata.id);
    const pagesArray = Array.isArray(legacyProject.pages)
      ? legacyProject.pages
      : [];

    const storedPageIdx = readStoredPageIndex(exampleIndex);
    const currentPageIdx =
      typeof legacyProject.currentPageId === "string"
        ? pagesArray.findIndex((p: any) => p.id === legacyProject.currentPageId)
        : -1;

    let selectedIdx = storedPageIdx ?? currentPageIdx;
    if (!Number.isInteger(selectedIdx) || selectedIdx < 0) {
      selectedIdx = pagesArray.length > 0 ? 0 : -1;
    }
    if (selectedIdx >= pagesArray.length) {
      selectedIdx = pagesArray.length - 1;
    }

    const pageSummaries: PageSummary[] = [];
    const pageDetails: Page[] = [];

    pagesArray.forEach((pageData: any, idx: number) => {
      const summary = createPageSummary(pageData, projectId, idx);
      const detail = createPageDetail(pageData, summary);
      pageSummaries.push(summary);
      pageDetails.push(detail);
    });

    const selectedPageDetail =
      selectedIdx >= 0 ? pageDetails[selectedIdx] ?? null : null;

    if (selectedIdx >= 0) {
      writeStoredPageIndex(exampleIndex, selectedIdx);
    }

    return {
      id: projectId,
      name: String(legacyProject.metadata.name ?? "Example Project"),
      description:
        typeof legacyProject.metadata.description === "string"
          ? legacyProject.metadata.description
          : undefined,
      projectType: (legacyProject.metadata.projectType ||
        "website") as ProjectType,
      appLanguages: ensureLanguages(legacyProject.metadata.appLanguages),
      createdAt: ensureDate(legacyProject.metadata.created),
      updatedAt: ensureDate(legacyProject.metadata.modified),
      pages: pageSummaries,
      selectedPage: selectedPageDetail ?? undefined,
      sketches: [],
      selectedSketch: undefined,
    };
  };

  const resolveActiveProject = () => {
    if (sessionCache?.currentProject?.project) {
      const bootstrap = sessionCache.currentProject
        .project as ProjectBootstrapPayload;
      const pages = Array.isArray(bootstrap.pages) ? bootstrap.pages : [];
      const sketches = Array.isArray(bootstrap.sketches)
        ? bootstrap.sketches
        : [];
      const selectedPage =
        (bootstrap.selectedPage as Page | undefined) ?? undefined;
      const selectedSketch =
        (bootstrap.selectedSketch as Sketch | undefined) ?? undefined;
      const selectedPageId =
        selectedPage?.id ?? pages.find((p: PageSummary) => p)?.id ?? null;
      const selectedPageSummary =
        selectedPageId != null
          ? pages.find((p: PageSummary) => p.id === selectedPageId) ?? null
          : null;
      const screenState =
        (sessionCache.currentProject as any)?.screenState ?? null;
      const screenSelectedModeRaw = (() => {
        if (!screenState || typeof screenState !== "object") return null;
        const mode = (screenState as Record<string, unknown>).selectedMode;
        if (typeof mode === "string") return mode;
        const legacy = (screenState as Record<string, unknown>).selected_mode;
        if (typeof legacy === "string") return legacy;
        return null;
      })();
      const screenSelectedMode = (() => {
        switch (screenSelectedModeRaw) {
          case "sketch":
          case "edit":
          case "ds":
          case "cms":
            return screenSelectedModeRaw;
          default:
            return null;
        }
      })();
      const screenSelectedTool = (() => {
        if (!screenState || typeof screenState !== "object") return null;
        const tool = (screenState as Record<string, unknown>).selectedTool;
        if (typeof tool === "string") return tool;
        const legacy = (screenState as Record<string, unknown>).selected_tool;
        if (typeof legacy === "string") return legacy;
        return null;
      })();
      const screenSelectedSketchId = (() => {
        if (!screenState || typeof screenState !== "object") return null;
        const candidates = [
          (screenState as Record<string, unknown>).currentSketchId,
          (screenState as Record<string, unknown>).currentSketch,
          (screenState as Record<string, unknown>).selectedSketchId,
          (screenState as Record<string, unknown>).lastSketchId,
        ];
        for (const candidate of candidates) {
          if (typeof candidate === "string" && candidate.length) {
            return candidate;
          }
        }
        return null;
      })();

      const activeMode = screenSelectedMode ?? "edit";

      const allowedTools = activeMode
        ? getSubtoolsForMode(activeMode as any)
        : [];
      const preferredTool =
        allowedTools.length &&
        screenSelectedTool &&
        allowedTools.includes(screenSelectedTool as any)
          ? screenSelectedTool
          : null;

      const selectedSketchId =
        selectedSketch?.id ??
        (bootstrap as any)?.selectedSketchId ??
        screenSelectedSketchId ??
        sketches[0]?.id ??
        null;
      const resolvedSelectedSketch =
        selectedSketch && selectedSketchId === selectedSketch.id
          ? selectedSketch
          : null;

      return {
        source: "session" as const,
        bootstrap,
        pages,
        sketches,
        selectedPage: selectedPage ?? null,
        selectedPageSummary,
        selectedSketch: resolvedSelectedSketch,
        selectedSketchId,
        availableLanguages: ensureLanguages(bootstrap.appLanguages),
        preferredTool,
        selectedMode: activeMode,
      };
    }

    const bootstrap = buildExampleBootstrap(selectedProjectIndex);
    const pages = Array.isArray(bootstrap.pages) ? bootstrap.pages : [];
    const selectedPage =
      (bootstrap.selectedPage as Page | undefined) ??
      (pages[0]
        ? createPageDetail(
            {
              id: pages[0].id,
              name: pages[0].name,
              type: pages[0].type,
              version: pages[0].version,
              draft: pages[0].draft,
              description: "",
              data: {},
              created: pages[0].updatedAt,
              modified: pages[0].updatedAt,
            },
            pages[0]
          )
        : undefined);
    const selectedPageSummary =
      selectedPage != null
        ? pages.find((p: PageSummary) => p.id === selectedPage.id) ?? null
        : pages[0] ?? null;

    return {
      source: "example" as const,
      bootstrap,
      pages,
      sketches: [],
      selectedPage: selectedPage ?? null,
      selectedPageSummary,
      selectedSketch: null,
      selectedSketchId: null,
      availableLanguages: ensureLanguages(bootstrap.appLanguages),
      preferredTool: null,
      selectedMode: "edit" as const,
    };
  };

  const emitProjectsAvailable = () => {
    let projects: ProjectSummary[] = [];
    let selectedProjectId: string | null = null;

    if (sessionCache?.projectList?.length) {
      projects = sessionCache.projectList.map((proj: any) =>
        coerceProjectSummary(proj)
      );
      selectedProjectId =
        sessionCache.currentProject?.project?.id ?? projects[0]?.id ?? null;
    } else {
      projects = examples.map((_example, idx) =>
        toProjectSummary(buildExampleBootstrap(idx))
      );
      selectedProjectId = projects[selectedProjectIndex]?.id ?? null;
    }

    appContext.eventBus.emit<ProjectsAvailableEventData>("projects-available", {
      projects,
      selectedProjectId,
    });
  };

  const emitPageChanged = (
    pageDetail: Page | null,
    pageSummary: PageSummary | null
  ) => {
    const pageId = pageDetail?.id ?? pageSummary?.id ?? null;
    if (!pageId) return;
    const pageName = pageDetail?.name ?? pageSummary?.name ?? "Untitled Page";
    uiState.setPage(pageName);

    const eventData: PageChangedEventData = {
      pageId,
      pageName,
      ...(pageDetail?.type || pageSummary?.type
        ? {
            pageType: (pageDetail?.type ??
              pageSummary?.type) as PageSummary["type"],
          }
        : {}),
      ...(pageSummary ? { pageSummary } : {}),
      ...(pageDetail ? { page: pageDetail } : {}),
    };

    appContext.eventBus.emit<PageChangedEventData>("page-changed", eventData);
  };

  const emitSketchChanged = (
    projectId: string,
    sketchDetail: Sketch | null,
    sketchSummary: SketchSummary | null
  ) => {
    const sketchId = sketchDetail?.id ?? sketchSummary?.id ?? null;
    if (!sketchId) return;
    const sketchName =
      sketchDetail?.title ??
      sketchSummary?.title ??
      "Untitled Sketch";

    const eventData: SketchChangedEventData = {
      projectId,
      sketchId,
      sketchName,
      ...(sketchSummary ? { sketchSummary } : {}),
      ...(sketchDetail ? { sketch: sketchDetail } : {}),
    };

    appContext.eventBus.emit<SketchChangedEventData>(
      "sketch-changed",
      eventData
    );
  };

  const emitProjectChanged = () => {
    const state = resolveActiveProject();
    const selectedPageId = state.selectedPageSummary?.id ?? null;

    uiState.setProject(state.bootstrap.name);
    let activeMode: "edit" | "sketch" | "ds" | "cms" | null =
      state.selectedMode ?? null;

    if (activeMode) {
      try {
        uiState.setMode(activeMode as any);
      } catch {
        activeMode = null;
      }
    }

    if (!activeMode) {
      try {
        uiState.setMode("edit" as any);
        activeMode = "edit";
      } catch {
        activeMode = null;
      }
    }

    const allowedSubtools =
      activeMode != null ? getSubtoolsForMode(activeMode as any) : [];

    if (allowedSubtools.length) {
      const desired =
        state.preferredTool &&
        allowedSubtools.includes(state.preferredTool as any)
          ? state.preferredTool
          : allowedSubtools[0];
      if (desired) {
        try {
          uiState.setToolSub(desired as any);
        } catch {
          // ignore invalid tool values
        }
      }
    } else {
      try {
        uiState.setToolSub(null);
      } catch {
        // ignore failures
      }
    }

    emitPageChanged(state.selectedPage, state.selectedPageSummary);

    const projectEventData: ProjectChangedEventData = {
      projectId: state.bootstrap.id,
      projectName: state.bootstrap.name,
      projectType: state.bootstrap.projectType,
      bootstrap: state.bootstrap,
      pages: state.pages,
      selectedPageId,
      ...(state.selectedPage ? { selectedPage: state.selectedPage } : {}),
      sketches: state.sketches,
      selectedSketchId: state.selectedSketchId ?? null,
      ...(state.selectedSketch ? { selectedSketch: state.selectedSketch } : {}),
      selectedMode: state.selectedMode ?? null,
      availableLanguages: state.availableLanguages,
      ...(state.preferredTool !== null
        ? { preferredTool: state.preferredTool }
        : {}),
    };

    appContext.eventBus.emit<ProjectChangedEventData>(
      "project-changed",
      projectEventData
    );
  };

  const restorePersistedSelection = () => {
    if (!sessionCache) {
      readStoredProjectIndex();
    }
    emitProjectsAvailable();
    emitProjectChanged();
  };

  const handleProjectSelected = (data: { projectId: string }) => {
    if (isDestroyed) return;

    if (sessionCache) {
      const currentId = sessionCache.currentProject?.project?.id;
      if (currentId && currentId === data.projectId) {
        emitProjectsAvailable();
        emitProjectChanged();
        return;
      }

      (async () => {
        try {
          const deviceId =
            (appContext as any)?.deviceInfo?.userAgent || undefined;
          const nextSession = await switchCurrentProject({
            projectId: data.projectId,
            deviceId,
          });
          sessionCache = nextSession;
          try {
            const sessMgr = (appContext as any)?.session;
            if (sessMgr && typeof sessMgr.setSession === "function") {
              sessMgr.setSession(nextSession);
            } else {
              appContext.eventBus.emit("session:updated", {
                session: nextSession,
              });
            }
          } catch {
            // ignore session propagation failure
          }
        } catch (error) {
          console.warn("Failed to switch project", error);
          emitProjectsAvailable();
          emitProjectChanged();
        }
      })();
      return;
    }

    const idx = examples.findIndex(
      (example) => example.project.getProject().metadata.id === data.projectId
    );
    if (idx >= 0) {
      selectedProjectIndex = idx;
      try {
        localStorage.setItem(SELECTED_PROJECT_INDEX_KEY, String(idx));
      } catch {
        // ignore persistence failures
      }
      emitProjectsAvailable();
      emitProjectChanged();
    }
  };

  const handlePageSelected = (data: { pageId: string }) => {
    if (isDestroyed) return;

    const state = resolveActiveProject();
    const targetSummary =
      state.pages.find((p: PageSummary) => p.id === data.pageId) ?? null;
    const targetDetail =
      state.selectedPage && state.selectedPage.id === data.pageId
        ? state.selectedPage
        : null;

    if (sessionCache) {
      if (targetSummary) {
        emitPageChanged(targetDetail, targetSummary);
      }
      return;
    }

    if (!targetSummary) return;

    const pageIndex = state.pages.findIndex(
      (p: PageSummary) => p.id === data.pageId
    );
    if (pageIndex >= 0) {
      writeStoredPageIndex(selectedProjectIndex, pageIndex);
    }
    emitProjectChanged();
  };

  const handleSketchSelected = (data: { sketchId: string }) => {
    if (isDestroyed) return;

    const state = resolveActiveProject();
    const targetSummary =
      state.sketches.find((sketch) => sketch.id === data.sketchId) ?? null;
    const targetDetail =
      state.selectedSketch && state.selectedSketch.id === data.sketchId
        ? state.selectedSketch
        : null;
    const projectId = state.bootstrap.id;

    if (sessionCache?.currentProject?.project) {
      try {
        const project = sessionCache.currentProject.project as any;
        project.selectedSketchId = data.sketchId;
        if (targetDetail) {
          project.selectedSketch = targetDetail;
        }
      } catch {
        // ignore session updates
      }
    }

    const fallbackSummary =
      targetSummary ??
      ({
        id: data.sketchId,
        projectId,
        title: "Untitled Sketch",
        updatedAt: new Date().toISOString(),
        version: 0,
        lastModifiedBy: "system",
        previews: [],
      } as SketchSummary);
    emitSketchChanged(projectId, targetDetail, fallbackSummary);
  };

  const unsubscribeProjectSelected = appContext.eventBus.on<{
    projectId: string;
  }>("project-selected", handleProjectSelected);

  const unsubscribePageSelected = appContext.eventBus.on<{ pageId: string }>(
    "page-selected",
    handlePageSelected
  );

  const unsubscribeSketchSelected = appContext.eventBus.on<{ sketchId: string }>(
    "sketch-selected",
    handleSketchSelected
  );

  const unsubscribeSketchRenamed = appContext.eventBus.on<{
    projectId: string;
    sketchId: string;
    title: string;
  }>("sketch-renamed", (data) => {
    if (isDestroyed) return;
    const nextTitle = typeof data?.title === "string" ? data.title.trim() : "";
    if (!data?.projectId || !data?.sketchId || !nextTitle) return;

    if (sessionCache?.currentProject?.project) {
      const project = sessionCache.currentProject.project as any;
      if (project?.id !== data.projectId) {
        emitProjectChanged();
        return;
      }

      if (Array.isArray(project.sketches)) {
        project.sketches = project.sketches.map((sketch: any) =>
          sketch?.id === data.sketchId ? { ...sketch, title: nextTitle } : sketch
        );
      }

      if (project.selectedSketch?.id === data.sketchId) {
        project.selectedSketch = {
          ...project.selectedSketch,
          title: nextTitle,
        };
      }

      if (project.selectedSketchId === data.sketchId) {
        emitSketchChanged(
          data.projectId,
          (project.selectedSketch as Sketch | undefined) ?? null,
          (Array.isArray(project.sketches)
            ? project.sketches.find((item: any) => item?.id === data.sketchId)
            : null) as SketchSummary | null
        );
      }
    }

    emitProjectChanged();
  });

  const unsubscribeSketchUpdated = appContext.eventBus.on<{
    projectId: string;
    sketchId: string;
    patch?: Record<string, unknown>;
  }>("sketch-updated", (data) => {
    if (isDestroyed) return;
    if (!data?.projectId || !data?.sketchId) return;
    const patch = data.patch;
    if (!patch || typeof patch !== "object") return;

    const nextTitle = typeof patch.title === "string" ? patch.title.trim() : "";
    if (!nextTitle) return;

    appContext.eventBus.emit("sketch-renamed", {
      projectId: data.projectId,
      sketchId: data.sketchId,
      title: nextTitle,
      source: "socket",
    });
  });

  const unsubscribeRequestProjects = appContext.eventBus.on<{} | void>(
    "request-projects",
    () => emitProjectsAvailable()
  );

  const unsubscribeRequestCurrent = appContext.eventBus.on<{} | void>(
    "request-current-selection",
    () => emitProjectChanged()
  );

  const unsubscribeSession = appContext.eventBus.on(
    "session:updated",
    (payload: any) => {
      sessionCache = (payload?.session as Session) ?? null;
      restorePersistedSelection();
    }
  );

  restorePersistedSelection();

  const destroy = () => {
    if (isDestroyed) return;
    isDestroyed = true;
    unsubscribeProjectSelected();
    unsubscribePageSelected();
    unsubscribeSketchSelected();
    unsubscribeSketchRenamed();
    unsubscribeSketchUpdated();
    unsubscribeRequestProjects();
    unsubscribeRequestCurrent();
    unsubscribeSession();
  };

  return { destroy };
};
